import {useUpmPlant} from '@hconnect/upm'
import {
  Modal,
  ModalTitle,
  ModalContent,
  Button,
  ControlledInput,
  ControlledAutocompleteSelect,
  ControlledToggle
} from '@hm/fundamental'
import {mdiClose} from '@mdi/js'
import {useCallback, useState} from 'react'
import {Controller} from 'react-hook-form'
import {useTranslation} from 'react-i18next'

import {useAddInstallationForm} from './hooks'
import {ProductSelect} from '@refra/modules/common/components'

import {MaterialColorSelector} from '@refra/modules/common/components/Colors/MaterialColorSelector'
import {useCanEditColors} from '@refra/modules/common/hooks'
import {useRefraManufacturersOptions} from '@refra/modules/common/hooks/queries'
import {useUpdateRefractoryCustomization} from '@refra/modules/common/hooks/mutations/customizations'
import type {Refractory} from '@refra/modules/refra-catalog/types'
import {useUrlParam} from '@refra/modules/routing/hooks'

interface AddInstallationModalProps {
  fromMeter: string
  toMeter: string
  min: number
  max: number
  shutdownId: number
  interventionDate: string
}

export const AddInstallationModal: React.FC<AddInstallationModalProps> = ({
  fromMeter,
  toMeter,
  min,
  max,
  shutdownId,
  interventionDate
}) => {
  const {t} = useTranslation()

  const {
    formMethods: {control, formState, watch, setValue},
    isFormBeingProcessed,
    onSubmit,
    onClose
  } = useAddInstallationForm({fromMeter, toMeter, shutdownId})

  const currentManufacturerId = watch('manufacturerId')
  const currentFromMeter = watch('fromMeter')

  const plantId = useUrlParam('plantCode')
  const upmPlant = useUpmPlant({plantId})

  const {refraManufacturersOptions, areRefraManufacturersFetching} = useRefraManufacturersOptions()

  const [currentRefractory, setCurrentRefractory] = useState<Refractory | null>(null)

  const {mutate: updateCustomization} = useUpdateRefractoryCustomization()

  const handleColorChange = useCallback(
    (refractoryId: number, color: string) => {
      updateCustomization({
        plantUpmId: upmPlant.upmId,
        refractoryId,
        color
      })
    },
    [upmPlant.upmId, updateCustomization]
  )

  const handleColorReset = useCallback(
    (refractoryId: number) => {
      updateCustomization({
        plantUpmId: upmPlant.upmId,
        refractoryId,
        color: null
      })
    },
    [upmPlant.upmId, updateCustomization]
  )

  const handleRefractoryChange = useCallback((refractory: Refractory | null) => {
    setCurrentRefractory(refractory)
  }, [])

  const handleManufacturerChange = useCallback(
    (manufacturerId: (string | number)[]) => {
      setValue('refractoryId', undefined)
      setCurrentRefractory(null)
      return manufacturerId[0]
    },
    [setValue]
  )

  const canEditColors = useCanEditColors()

  const isFormFieldDisabled = isFormBeingProcessed || areRefraManufacturersFetching

  return (
    <Modal open onClose={() => onClose()} data-cy="refra_add_material_modal">
      <form onSubmit={onSubmit} className="space-y-4">
        <div className={'flex justify-between'}>
          <ModalTitle data-cy="refra_add_material_modal_title">
            {t('refraPlanning.materialInstallations.addMaterial')}
          </ModalTitle>
          <Button
            icon={mdiClose}
            variant="tertiary"
            disabled={isFormBeingProcessed}
            onClick={onClose}
            data-cy="refra_add_material_modal_close_btn"
          />
        </div>
        <ModalContent>
          <div className="flex flex-col gap-4">
            <Controller
              name="fromMeter"
              control={control}
              rules={{required: t('error.required')}}
              render={({field}) => (
                <ControlledInput
                  {...field}
                  label={t('refraPlanning.materialInstallations.fromMeter')}
                  error={formState.errors.fromMeter?.message}
                  isTouched={!!formState.touchedFields.fromMeter}
                  isDisabled={isFormFieldDisabled}
                  type={'number'}
                  min={min}
                  max={max}
                  step={0.01}
                  data-cy="add_material_from_meter_input"
                />
              )}
            />
            <Controller
              name="toMeter"
              control={control}
              rules={{
                required: t('error.required'),
                validate: (value) => Number(value) > Number(currentFromMeter) || t('error.toMeter')
              }}
              render={({field}) => (
                <ControlledInput
                  {...field}
                  label={t('refraPlanning.materialInstallations.toMeter')}
                  error={formState.errors.toMeter?.message}
                  isTouched={!!formState.touchedFields.toMeter}
                  isDisabled={isFormFieldDisabled}
                  type={'number'}
                  min={min}
                  max={max}
                  step={0.01}
                  data-cy="add_material_to_meter_input"
                />
              )}
            />
            <Controller
              name="manufacturerId"
              control={control}
              rules={{required: t('error.required')}}
              render={({field}) => (
                <ControlledAutocompleteSelect
                  label={t('refraPlanning.materialInstallations.supplier')}
                  options={refraManufacturersOptions}
                  selectedValues={[field.value ?? '']}
                  error={formState.errors.manufacturerId?.message}
                  isDisabled={isFormFieldDisabled}
                  isTouched={!!formState.errors.manufacturerId?.message}
                  onChange={(manufacturerId) => {
                    field.onChange(handleManufacturerChange(manufacturerId))
                  }}
                  data-cy="add_material_supplier_select"
                />
              )}
            />
            <Controller
              name="refractoryId"
              control={control}
              rules={{required: t('error.required')}}
              render={({field}) => (
                <ProductSelect
                  activeAfter={interventionDate}
                  manufacturerId={currentManufacturerId}
                  plantUpmId={upmPlant.upmId}
                  selectedValue={field.value}
                  error={formState.errors.refractoryId?.message}
                  isDisabled={isFormBeingProcessed}
                  isTouched={!!formState.errors.refractoryId?.message}
                  onChange={field.onChange}
                  onRefractoryChange={handleRefractoryChange}
                  autoFocus
                />
              )}
            />
            <div className="flex flex-col gap-4">
              {currentRefractory && (
                <MaterialColorSelector
                  refractoryId={currentRefractory.id}
                  globalColor={currentRefractory.color}
                  localColor={currentRefractory.customization?.color ?? undefined}
                  isDisabled={!canEditColors}
                  fullWidth={true}
                  showTitles={true}
                  showGlobalColor={true}
                  onColorChange={handleColorChange}
                  onColorReset={handleColorReset}
                />
              )}
            </div>
            <Controller
              name="isMortarRequired"
              control={control}
              render={({field}) => (
                <ControlledToggle
                  {...field}
                  label={t('refraPlanning.materialInstallations.mortar')}
                  checked={Boolean(field.value)}
                  value={undefined}
                  data-cy="add_material_is_mortar_switch"
                  disabled={isFormFieldDisabled}
                />
              )}
            />
          </div>
        </ModalContent>
        <div className={'space-x-2 flex justify-end'}>
          <Button type="submit" disabled={isFormFieldDisabled} data-cy="add_material_btn">
            {t('refraPlanning.materialInstallations.addMaterial')}
          </Button>
        </div>
      </form>
    </Modal>
  )
}
